import { useQuery } from "@tanstack/react-query";
import { thetaApi } from "@/lib/api/theta";

/** The metadata shape the onebox card renders from. */
interface OgData {
  title: string | null;
  description: string | null;
  image: string | null;
  favicon: string | null;
}

/**
 * OpenGraph metadata for `url`, from `GET /api/v1/link-previews`.
 *
 * THE LEGACY PATH. This serves `LinkPreview`, which renders a Discourse onebox
 * whose card carried no title of its own — a case that only exists in migrated
 * posts. New content goes through `useResolvedLinkPreview` and the reshaped
 * card. Kept because those posts are real.
 *
 * It used to call `/api/og`, a Next.js route, which is now deleted: unfurling
 * moved into the API so the Expo app has something to call (§2.9). `favicon` is
 * always null because the API does not return one; the card treats it as
 * optional and simply omits the icon.
 */
export function useLinkPreview(url: string, enabled = true) {
  return useQuery<OgData>({
    enabled,
    queryKey: ["link-preview", url],
    queryFn: async () => {
      const preview = await thetaApi.linkPreview(url);
      if (!preview || preview.kind !== "external") {
        throw new Error("No preview");
      }
      return {
        title: preview.title,
        description: preview.description,
        image: preview.imageUrl,
        favicon: null,
      };
    },
    staleTime: 60 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}
